import React from "react";

interface UsePollingProps<T> {
  // ポーリング対象のデータ
  data: T[];
  // データを取得する関数
  fetchData: (id: number) => void;
  // ポーリングを実行するかどうか
  isActive: boolean;
  // 対象のID
  targetId: number | null;
  // ポーリング間隔（ミリ秒）
  interval?: number;
  // ポーリング継続の条件を判定する関数
  shouldContinuePolling: (data: T[]) => boolean;
  // ポーリング開始の条件を判定する関数（オプション）
  shouldStartPolling?: (data: T[]) => boolean;
}

export const usePolling = <T>({
  data,
  fetchData,
  isActive,
  targetId,
  interval = 5000,
  shouldContinuePolling,
  shouldStartPolling = () => true,
}: UsePollingProps<T>) => {
  const pollingRef = React.useRef<number | null>(null);
  const currentTargetIdRef = React.useRef<number | null>(null);
  const shouldContinue = shouldContinuePolling(data);
  const shouldStart = shouldStartPolling(data);

  // ポーリング開始
  const startPolling = React.useCallback(
    (id: number) => {
      const poll = () => {
        if (currentTargetIdRef.current === id && isActive) {
          fetchData(id);
          pollingRef.current = window.setTimeout(poll, interval);
        }
      };

      pollingRef.current = window.setTimeout(poll, interval);
    },
    [fetchData, isActive, interval],
  );

  // ポーリング停止
  const stopPolling = React.useCallback(() => {
    if (pollingRef.current) {
      window.clearTimeout(pollingRef.current);
      pollingRef.current = null;
      currentTargetIdRef.current = null;
    }
  }, []);

  // ポーリング制御
  React.useEffect(() => {
    if (shouldContinue && shouldStart && targetId && targetId > 0 && isActive) {
      currentTargetIdRef.current = targetId;
      stopPolling();
      startPolling(targetId);
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [
    shouldContinue,
    shouldStart,
    targetId,
    isActive,
    startPolling,
    stopPolling,
  ]);

  // クリーンアップ
  React.useEffect(() => {
    return stopPolling;
  }, [stopPolling]);

  return {
    isPolling: pollingRef.current !== null,
    shouldContinue,
    shouldStart,
    startPolling,
    stopPolling,
  };
};
